/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CommonStyle, GameExperiencePayloadDto } from '@luckycart/xpn-types';

import { getDefaultConfigurations } from '../utils/Configs';
import { Logger } from '../utils/Logger';
import { ScreenTypes } from '../utils/ScreensList';
import { InformationContext, InformationType } from '../utils/contexts/information.context';
import { StyleContext } from '../utils/contexts/style.context';

import { CollectScreen } from '../templates/screens/CollectScreen';
import { HomeScreen } from '../templates/screens/HomeScreen';
import { GameScreen } from '../templates/screens/GameScreen';
import { ErrorScreen } from '../templates/screens/ErrorScreen';
import { ExpiredScreen } from '../templates/screens/ExpiredScreen';
import { UnavailableScreen } from '../templates/screens/UnavailableScreen';
import { WonScreen } from '../templates/screens/WonScreen';
import { LostScreen } from '../templates/screens/LostScreen';
import { NextScreen } from '../templates/screens/NextScreen';
import { AlreadyPlayedScreen } from '../templates/screens/AlreadyPlayedScreen';
import { SingleGameScreen } from '../templates/screens/SingleGameScreen';
import { MultiGameScreen } from '../templates/screens/MultiGameScreen';
import { Loader } from '../templates/common-components/Loader';
import { DateTime } from 'luxon';

import { useAppDispatch, useAppSelector } from '../hooks';

import { CollectFormValues } from '../common-types/CollectForm.type';

import {
  displayErrorScreen,
  transitionAfterCollectScreen,
  transitionAfterWelcomeScreen,
  transitionFromGameHomeToGameScreen, transitionFromGameResultToNextGame, transitionFromGameToGameResult, transitionFromLoadingToFirstScreen,
} from '../slices/DisplayedScreen';
import { setCurrentGameConfiguration, setGameResult } from '../slices/GameExperience';

import { experienceServiceInstance } from '../utils/services/ExperienceService';
import { collectServiceInstance } from '../utils/services/CollectService';
import { apiServiceInstance } from '../utils/services/ApiService';
import { personalDataServiceInstance } from '../utils/services/PersonalDataService';
import { setRetailerPersonalData } from '../slices/GlobalRetailerPersonalData';
import { setInitialExperienceData } from '../slices/GlobalExperience';
import { smartOptinsServiceInstance } from '../utils/services/SmartOptinsExperienceService';
import { setSmartOptins } from '../slices/SmartOptins';
import { Collect } from './SmartOptinsCollect';

export const ExperienceController: React.FunctionComponent = () => {
  const [informationContext, setInformationContext] = useState<InformationType>({});
  const [styleContext, setStyleContext] = useState<CommonStyle | null>(null);
  const [searchParams] = useSearchParams();
  const SU_SITE_KEY = 'nhQxzLVX';

  const {
    siteExperienceConfiguration,
    thumbnails,
    games,
  } = useAppSelector((state) => state.globalExperience);
  const {
    hasWonCurrentGame,
    currentOperation,
    currentGameConfiguration,
    currentShopperExperience,
    currentIndex,
  } = useAppSelector((state) => state.gameExperience);
  const {
    displayedScreen,
    displayError,
    isLoading,
  } = useAppSelector((state) => state.displayScreen);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const siteKey = searchParams.get('siteKey');
    const shopperId = searchParams.get('shopperId');
    const cartId = searchParams.get('cartId');
    const experienceId = searchParams.get('experienceId');
    const retailerOptinsDbToken = searchParams.get('token');
    //const retailerOptinsDbToken = 'token';
	
    if (siteKey === null || shopperId === null || cartId === null) {
      Logger.log('Missing data to display the experience');
      dispatch(displayErrorScreen());
      return;
    }
    Promise.all([
      // Rmove retailerOptinsDbToken to fix prefilled email
      personalDataServiceInstance.getCustomerPersonalData(siteKey, shopperId, retailerOptinsDbToken),
      experienceServiceInstance.getSiteInformations(siteKey),
      experienceServiceInstance.getGamesInformations(shopperId, siteKey, cartId, experienceId),
      smartOptinsServiceInstance.getSmartOptinExperience(shopperId, siteKey),
    ])
      .then(([personalData, siteInformation, gamesInformations, smartOptinsExperience]) => {
        if (
          gamesInformations.firstGameData === null ||
          gamesInformations.firstGameData === undefined ||
          siteInformation.site === undefined || siteInformation.site === null
        ) {
          Logger.log('No game found');
          dispatch(displayErrorScreen());
          return;
        }
        dispatch(setRetailerPersonalData({ retailerPersonalData: personalData }));
        dispatch(setSmartOptins({ smartOptinsExperience }));
        dispatch(setInitialExperienceData({
          games: gamesInformations.games,
          siteConfiguration: siteInformation.config.screens,
          site: siteInformation.site,
        }));
        dispatch(setCurrentGameConfiguration({
          data: gamesInformations.firstGameData,
          index: 0,
        }));
        const { playLimitDate } = gamesInformations.firstGameData.experience.payload as GameExperiencePayloadDto;
        dispatch(transitionFromLoadingToFirstScreen({
          firstGameIsUnavailable: !(gamesInformations.firstGameData.experience.payload as GameExperiencePayloadDto).isAvailable,
          firstGameHasBeenPlayed: (gamesInformations.firstGameData.experience.payload as GameExperiencePayloadDto).isConsumed ?? false,
          firstGameHasExpired: playLimitDate !== undefined && DateTime.fromISO(playLimitDate).isValid && DateTime.fromISO(playLimitDate) < DateTime.now(),
          hasOnlyOneGame: gamesInformations.games.length === 1,
          siteConfiguration: siteInformation.config.screens,
        }));
        setInformationContext((previous: InformationType) => ({
          ...previous,
          gameCount: gamesInformations.games.length,
        }));
        setStyleContext(siteInformation.config.commons.style);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentShopperExperience !== null && currentOperation !== null) {
      setInformationContext((previous: InformationType) => ({
        ...previous,
        operation: currentOperation,
        experience: currentShopperExperience,
      }));
    }
  }, [currentShopperExperience, currentOperation]);

  if (displayError || displayedScreen === ScreenTypes.ERROR) {
    return <ErrorScreen config={getDefaultConfigurations().error}/>;
  }

  if (isLoading) {
    return <Loader/>;
  }

  return <InformationContext.Provider value={{
    information: informationContext,
    setInformation: setInformationContext,
  }}>
    <StyleContext.Provider value={{
      setStyle: setStyleContext,
      style: styleContext,
    }}>
      {
        displayedScreen === ScreenTypes.SINGLEGAME && siteExperienceConfiguration !== null &&
        <SingleGameScreen
          ctaOnClick={(): void => {
            if (siteExperienceConfiguration?.smartOptinsForm.activated && searchParams.get('token') !== null) {
              dispatch(transitionAfterWelcomeScreen({ 
                collectScreenIsActivated: siteExperienceConfiguration?.collect.activated, 
                isSmartOptin: siteExperienceConfiguration?.smartOptinsForm.activated, 
              }));
              return;
            }
            dispatch(transitionAfterWelcomeScreen({ collectScreenIsActivated: siteExperienceConfiguration?.collect.activated, isSmartOptin: false }));
          }}
          config={siteExperienceConfiguration.singleGame}
          thumbnailUrls={thumbnails}
          currentShopperExperience={currentShopperExperience}
        />
      }
      {
        displayedScreen === ScreenTypes.MULTIGAME && siteExperienceConfiguration !== null &&
        <MultiGameScreen
          ctaOnClick={(): void => {
            if (siteExperienceConfiguration?.smartOptinsForm.activated && searchParams.get('token') !== null) {
              dispatch(transitionAfterWelcomeScreen({ 
                collectScreenIsActivated: siteExperienceConfiguration?.collect.activated, 
                isSmartOptin: siteExperienceConfiguration?.smartOptinsForm.activated, 
              }));
              return;
            }
            dispatch(transitionAfterWelcomeScreen({ collectScreenIsActivated: siteExperienceConfiguration?.collect.activated, isSmartOptin: false }));
          }}
          config={siteExperienceConfiguration.multiGame}
          thumbnailUrls={thumbnails}
          currentShopperExperience={currentShopperExperience}
        />
      }
      
      {
        displayedScreen === ScreenTypes.SMART_COLLECT && siteExperienceConfiguration !== null && <Collect />
      }
      {
        displayedScreen === ScreenTypes.COLLECT && siteExperienceConfiguration !== null &&
        <CollectScreen
          suOptinForm={false}
          siteKey={searchParams.get('siteKey')}
          config={siteExperienceConfiguration.collect}
          onSubmit={(values: CollectFormValues): void => {
            if (currentShopperExperience !== null) {
              const { experienceId, siteKey, shopperId } = currentShopperExperience;
              collectServiceInstance.sendConsents(
                experienceId,
                values.consents,
                siteKey,
                shopperId
              ).then(() => {
                collectServiceInstance.sendPersonalData(
                  experienceId,
                  values.personalData,
                  siteKey,
                  shopperId
                ).then(() => {
                  dispatch(transitionAfterCollectScreen());
                })
                  .catch((err) => {
                    Logger.log(err);
                    dispatch(displayErrorScreen());
                  });
              })
                .catch((err) => {
                  Logger.log(err);
                  dispatch(displayErrorScreen());
                });
            }
          }}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.HOME &&
        <HomeScreen
          isSuScreen={searchParams.get('siteKey') === SU_SITE_KEY}
          config={currentGameConfiguration.home}
          ctaOnClick={(): void => {
            dispatch(transitionFromGameHomeToGameScreen());
          }}
          currentShopperExperience={currentShopperExperience}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.GAME &&
        <GameScreen
          config={currentGameConfiguration.game}
          hasWon={hasWonCurrentGame}
          onGameBegin={(): void => {
            const ShopperId = searchParams.get('shopperId');
            const siteKey = searchParams.get('siteKey');
            if (currentShopperExperience === null || ShopperId === null || siteKey === null) {
              Logger.log('Missing experience data to display the experience');
              dispatch(displayErrorScreen());
            } else {
              apiServiceInstance.fetchGameResult(currentShopperExperience.experienceId, ShopperId, siteKey)
                .then((response) => {
                  dispatch(setGameResult(response.hasWon));
                  setInformationContext((previous: InformationType) => {
                    return {
                      ...previous,
                      gamePlayed: response,
                    } as unknown as InformationType;
                  });
                })
                .catch((err) => {
                  Logger.log(err);
                  dispatch(displayErrorScreen());
                });
            }
          }}
          onGameDone={(): void => {
            if (hasWonCurrentGame === null) {
              Logger.log('No information to display result screen');
              dispatch(displayErrorScreen());
              return;
            }
            dispatch(transitionFromGameToGameResult({ hasWon: hasWonCurrentGame }));
          }}
        />
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.WON &&
        <WonScreen config={currentGameConfiguration.won}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.LOST &&
        <LostScreen config={currentGameConfiguration.lost}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.EXPIRED &&
        <ExpiredScreen config={currentGameConfiguration.expired}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.ALREADY_PLAYED &&
        <AlreadyPlayedScreen config={currentGameConfiguration.alreadyPlayed}/>
      }
      {
        currentGameConfiguration !== null && displayedScreen === ScreenTypes.UNAVAILABLE &&
        <UnavailableScreen config={currentGameConfiguration.unavailable}/>
      }
      {
        currentGameConfiguration !== null &&
        (displayedScreen === ScreenTypes.WON || displayedScreen === ScreenTypes.LOST ) &&
        games[currentIndex + 1] !== undefined &&
        <NextScreen
          config={games[currentIndex + 1].gameConfiguration.next}
          ctaOnClick={(): void => {
            const newIndex = currentIndex + 1;
            dispatch(setCurrentGameConfiguration({
              data: games[newIndex],
              index: newIndex,
            }));
            dispatch(transitionFromGameResultToNextGame());
          }}
        />
      }
    </StyleContext.Provider>
  </InformationContext.Provider>;
};
